import {
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import { motion, type MotionProps } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { cva, type VariantProps } from 'class-variance-authority'
import { useKeyboardInteraction } from '../../hooks/use-keyboard-interaction'
import { CellContext, useCellContext } from './nested/context'
import { CellHeader } from './nested/header'
import { CellIcon } from './nested/icon'
import { CellTitle } from './nested/title'
import { CellContent } from './nested/content'
import { CellFooter } from './nested/footer'
import { CellBanner } from './nested/banner'
import { CellMetadata } from './nested/metadata'
import { CellProgress } from './nested/progress'

const cellVariants = cva(
  [
    'group/cell outline-none relative flex flex-wrap items-center overflow-visible select-none shadow-sm rounded-2xl corner-smooth text-sm transition-opacity duration-150 bg-background-raised text-foreground opacity-100 gradient-stroke-inset before:bg-[linear-gradient(to_bottom,rgba(255,255,255,0.9),rgba(255,255,255,0))] dark:before:bg-[linear-gradient(to_bottom,rgba(255,255,255,0.35),rgba(255,255,255,0))]',
    // No border: shadow-sm already draws the edge, and a border on top of it doubles the outline.
  ],
  {
    variants: {
      size: {
        default: 'p-3 gap-3',
        compact: 'p-3',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

interface CellProps
  extends
    VariantProps<typeof cellVariants>,
    Omit<ComponentPropsWithoutRef<'div'>, 'onClick' | 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onAnimationStart'> {
  isPending?: boolean
  clickable?: boolean
  focused?: boolean
  onClick?: (e: MouseEvent<HTMLDivElement>) => void
  layoutId?: string
  initial?: MotionProps['initial']
  animate?: MotionProps['animate']
  transition?: MotionProps['transition']
}

// Hover lifts the cell to shadow-md. The shadow sits on ::after and fades by opacity, so the hover
// animates on the compositor rather than repainting a box-shadow each frame.
const HOVER_LIFT =
  'cursor-pointer after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:corner-smooth after:shadow-md after:opacity-0 after:transition-opacity after:duration-150 hover:after:opacity-100 motion-reduce:after:transition-none'

function Cell({
  className,
  style,
  children,
  isPending = false,
  size = 'default',
  clickable = true,
  focused: focusedProp = false,
  // Defaulted here, not in the props spread, so a wrapper forwarding `tabIndex={undefined}` can't clear it.
  tabIndex = 0,
  onClick,
  onKeyDown,
  onFocus,
  onBlur,
  onPointerDown,
  layoutId,
  initial,
  animate,
  transition,
  ...props
}: CellProps) {
  const isKeyboardInteraction = useKeyboardInteraction()
  const [keyboardFocused, setKeyboardFocused] = useState(false)
  const focused = focusedProp || keyboardFocused

  const contextValue = useMemo(() => ({ size }), [size])

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      const selection = window.getSelection()
      if (selection && selection.toString().length > 0) {
        return
      }
      onClick(e)
    }
  }

  // Shared props for both the plain and motion variants. Most cells (lists,
  // history, actions) never animate, so they render a plain <div> and skip
  // framer-motion's per-render layout/animation bookkeeping entirely.
  const commonProps = {
    onClick: clickable && onClick ? handleClick : undefined,
    tabIndex,
    role: clickable && onClick ? 'button' : undefined,
    onPointerDown,
    onFocus: (e: FocusEvent<HTMLDivElement>) => {
      if (isKeyboardInteraction()) setKeyboardFocused(true)
      onFocus?.(e)
    },
    onBlur: (e: FocusEvent<HTMLDivElement>) => {
      setKeyboardFocused(false)
      onBlur?.(e)
    },
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e)
      if (e.defaultPrevented) return
      const target = e.target as HTMLElement
      const isEditable = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable
      if (isEditable && (e.key === 'Enter' || e.key === ' ')) return
      if (clickable && onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        onClick(e as unknown as MouseEvent<HTMLDivElement>)
      }
    },
    'data-slot': 'cell',
    className: twMerge(
      cellVariants({ size }),
      isPending || !clickable || !onClick ? 'cursor-default opacity-100' : HOVER_LIFT,
      // The system ring, for a cell focused by list navigation as well as by the DOM.
      focused && 'focus-ring',
      className,
    ),
    style,
    ...props,
  }

  // Note: animation props must be stable for a cell's lifetime — flipping
  // `animated` swaps the root element (motion.div <-> div) and remounts the
  // entire subtree.
  const animated = Boolean(layoutId || initial || animate || transition)

  return (
    <CellContext.Provider value={contextValue}>
      {animated ? (
        <motion.div
          layoutId={layoutId}
          layout={layoutId ? 'position' : undefined}
          initial={initial}
          animate={animate}
          transition={transition}
          {...commonProps}
        >
          {children}
        </motion.div>
      ) : (
        <div {...commonProps}>{children}</div>
      )}
    </CellContext.Provider>
  )
}

Cell.Header = CellHeader
Cell.Icon = CellIcon
Cell.Title = CellTitle
Cell.Content = CellContent
Cell.Footer = CellFooter
Cell.Banner = CellBanner
Cell.Metadata = CellMetadata
Cell.Progress = CellProgress

export { Cell, cellVariants, useCellContext }
export { UnreadDot, unreadDotCutoutStyle } from './nested/icon'
export type { CellProps }
